import { apiClient } from './apiClient';

// Tipos
export interface Role {
  id: number;
  name: string;
  description: string | null;
  isDefault: boolean;
}

export interface Permission {
  id: number;
  code: string;
  name: string;
  description: string | null;
  category: string;
}

export interface CreateRoleDto {
  name: string;
  description?: string;
  isDefault?: boolean;
}

export interface UpdateRoleDto {
  name?: string;
  description?: string;
  isDefault?: boolean;
}

export interface CreatePermissionDto {
  code: string;
  name: string;
  description?: string;
  category: string;
}

export interface UpdatePermissionDto {
  code?: string;
  name?: string;
  description?: string;
  category?: string;
}

// Cache para almacenar permisos y evitar llamadas repetidas
let permissionsCache: {
  data: Permission[];
  timestamp: number;
  expiresInMs: number;
} | null = null;

// Cache para almacenar permisos de roles y evitar llamadas repetidas
const rolePermissionsCache: {
  [roleId: number]: {
    data: number[];
    timestamp: number;
    expiresInMs: number;
  };
} = {};

/**
 * Obtener todos los roles
 */
export const getRoles = async (): Promise<Role[]> => {
  try {
    const response = await apiClient.get<{ success: boolean; data: Role[] }>('/auth/roles');
    return response.data.data;
  } catch (error) {
    console.error('Error al obtener roles:', error);
    throw error;
  }
};

/**
 * Obtener un rol por su ID
 */
export const getRoleById = async (id: number): Promise<Role> => {
  try {
    const response = await apiClient.get<{ success: boolean; data: Role }>(`/auth/roles/${id}`);
    return response.data.data;
  } catch (error) {
    console.error(`Error al obtener rol con ID ${id}:`, error);
    throw error;
  }
};

/**
 * Crear un nuevo rol
 */
export const createRole = async (roleData: CreateRoleDto): Promise<Role> => {
  try {
    const response = await apiClient.post<{ success: boolean; data: Role }>('/auth/roles', roleData);
    return response.data.data;
  } catch (error) {
    console.error('Error al crear rol:', error);
    throw error;
  }
};

/**
 * Actualizar un rol existente
 */
export const updateRole = async (id: number, roleData: UpdateRoleDto): Promise<Role> => {
  try {
    const response = await apiClient.put<{ success: boolean; data: Role }>(`/auth/roles/${id}`, roleData);
    return response.data.data;
  } catch (error) {
    console.error(`Error al actualizar rol con ID ${id}:`, error);
    throw error;
  }
};

/**
 * Eliminar un rol
 */
export const deleteRole = async (id: number): Promise<void> => {
  try {
    await apiClient.delete(`/auth/roles/${id}`);
  } catch (error) {
    console.error(`Error al eliminar rol con ID ${id}:`, error);
    throw error;
  }
};

/**
 * Obtener todos los permisos
 */
export const getPermissions = async (): Promise<Permission[]> => {
  try {
    // Verificar si hay caché válida (5 minutos)
    const now = Date.now();
    if (
      permissionsCache &&
      now - permissionsCache.timestamp < permissionsCache.expiresInMs
    ) {
      return permissionsCache.data;
    }

    const response = await apiClient.get<{ success: boolean; data: Permission[] }>('/auth/permissions');
    
    // Almacenar en caché
    permissionsCache = {
      data: response.data.data,
      timestamp: now,
      expiresInMs: 5 * 60 * 1000, // 5 minutos
    };
    
    return response.data.data;
  } catch (error) {
    console.error('Error al obtener permisos:', error);
    throw error;
  }
};

/**
 * Obtener un permiso por su ID
 */
export const getPermissionById = async (id: number): Promise<Permission> => {
  try {
    const response = await apiClient.get<{ success: boolean; data: Permission }>(`/auth/permissions/${id}`);
    return response.data.data;
  } catch (error) {
    console.error(`Error al obtener permiso con ID ${id}:`, error);
    throw error;
  }
};

/**
 * Crear un nuevo permiso
 */
export const createPermission = async (permissionData: CreatePermissionDto): Promise<Permission> => {
  try {
    const response = await apiClient.post<{ success: boolean; data: Permission }>('/auth/permissions', permissionData);
    // Invalidar caché después de crear
    invalidatePermissionsCache();
    return response.data.data;
  } catch (error) {
    console.error('Error al crear permiso:', error);
    throw error;
  }
};

/**
 * Actualizar un permiso existente
 */
export const updatePermission = async (id: number, permissionData: UpdatePermissionDto): Promise<Permission> => {
  try {
    const response = await apiClient.put<{ success: boolean; data: Permission }>(`/auth/permissions/${id}`, permissionData);
    // Invalidar caché después de actualizar
    invalidatePermissionsCache();
    return response.data.data;
  } catch (error) {
    console.error(`Error al actualizar permiso con ID ${id}:`, error);
    throw error;
  }
};

/**
 * Eliminar un permiso
 */
export const deletePermission = async (id: number): Promise<void> => {
  try {
    await apiClient.delete(`/auth/permissions/${id}`);
    // Invalidar caché después de eliminar
    invalidatePermissionsCache();
  } catch (error) {
    console.error(`Error al eliminar permiso con ID ${id}:`, error);
    throw error;
  }
};

/**
 * Obtener permisos asignados a un rol
 */
export const getRolePermissions = async (roleId: number): Promise<number[]> => {
  if (!roleId || isNaN(roleId)) {
    console.error('ID de rol inválido', roleId);
    return [];
  }

  try {
    // Verificar si hay caché válida (5 minutos)
    const now = Date.now();
    if (
      rolePermissionsCache[roleId] &&
      now - rolePermissionsCache[roleId].timestamp < rolePermissionsCache[roleId].expiresInMs
    ) {
      return [...rolePermissionsCache[roleId].data];
    }

    // Añadir un pequeño retraso para evitar múltiples llamadas rápidas
    await new Promise(resolve => setTimeout(resolve, 100));

    const response = await apiClient.get<{ success: boolean; data: number[] }>(`/auth/roles/${roleId}/permissions`);
    
    // Validar la respuesta
    const permissionIds = Array.isArray(response.data.data) ? response.data.data : [];
    
    // Almacenar en caché
    rolePermissionsCache[roleId] = {
      data: [...permissionIds],
      timestamp: now,
      expiresInMs: 5 * 60 * 1000, // 5 minutos
    };
    
    return permissionIds;
  } catch (error) {
    console.error(`Error al obtener permisos del rol ${roleId}:`, error);
    
    // Si hay un error pero tenemos caché, usar la caché anterior aunque haya expirado
    if (rolePermissionsCache[roleId]) {
      return [...rolePermissionsCache[roleId].data];
    }
    
    throw error;
  }
};

/**
 * Asignar permisos a un rol
 */
export const assignPermissionsToRole = async (roleId: number, permissionIds: number[]): Promise<void> => {
  try {
    // Invalidar caché después de asignar
    delete rolePermissionsCache[roleId];
    
    await apiClient.post(`/auth/roles/${roleId}/permissions`, { permissionIds });
  } catch (error) {
    console.error(`Error al asignar permisos al rol ${roleId}:`, error);
    throw error;
  }
};

/**
 * Obtener permisos predefinidos
 */
export const getPredefinedPermissions = async (): Promise<Permission[]> => {
  try {
    const response = await apiClient.get<{ success: boolean; data: Permission[] }>('/auth/predefined-permissions');
    return response.data.data;
  } catch (error) {
    console.error('Error al obtener permisos predefinidos:', error);
    throw error;
  }
};

/**
 * Sincronizar permisos predefinidos
 */
export const syncPredefinedPermissions = async (): Promise<void> => {
  try {
    await apiClient.post('/auth/sync-permissions');
    // Invalidar caché después de sincronizar
    invalidatePermissionsCache();
  } catch (error) {
    console.error('Error al sincronizar permisos predefinidos:', error);
    throw error;
  }
};

/**
 * Invalidar caché de permisos
 */
export const invalidatePermissionsCache = (): void => {
  permissionsCache = null;
}; 